import { ImageFile, OrderDirection, PaginationInfo } from '../shared';

import { Event } from './event';
import { graphqlApiWithPersisted } from './graphqlApi';
import { InAppMessageContent } from './inAppMessage';
import { Story } from './story';
import { StoryCard } from './storyCard';
import { User } from './user';

export enum ButtonLinkType {
  URL_BROWSER = 'URL_BROWSER',
  URL_APP = 'URL_APP',
  SHARE = 'SHARE',
  CANCEL = 'CANCEL',
}

export enum InAppMessagePlacement {
  POP_UP = 'POP_UP',
  FEED = 'FEED',
}

export enum NotificationsTabType {
  PUSH,
  INAPP,
}

export enum NotificationCommentAction {
  COMMENT = 'COMMENT',
  REPLY = 'REPLY',
  LIKE = 'LIKE',
  MENTION_IN_CARD = 'MENTION_IN_CARD',
}

export enum NotificationType {
  DEFAULT = 'DEFAULT',
  COMMENT = 'COMMENT',
  STORY_CARD_MENTION = 'STORY_CARD_MENTION',
  LEADER_BOARD_USER_CLOSE_TO_PROMOTION = 'LEADER_BOARD_USER_CLOSE_TO_PROMOTION',
  LEADER_BOARD_USER_PROMOTED_TO_NEW_BADGE = 'LEADER_BOARD_USER_PROMOTED_TO_NEW_BADGE',
  REFERRAL_USER_ACTIVATED = 'REFERRAL_USER_ACTIVATED',
}

export interface NotificationCommentItem {
  id: number;
  content: string;
  contentUpdatedAt: string;
  createdAt: string;
  authorId: number;
  author: User;
  story: Story;
  storyCard: StoryCard;
}

interface Notification {
  id: number;
  type: NotificationType;
  deliveredTime: string;
}

export interface NotificationDefault extends Notification {
  title: string;
  message: string;
  story: Story | null;
  storyCard: StoryCard | null;
  event: Event | null;
  createdTime: string;
  expirationTime: string;
  permissions: { allowToDelete: boolean; allowToUpdate: boolean };
  statistic: { open: number; sent: number };
  channelId?: number;
  url?: string;
}

export interface NotificationComment extends Notification {
  action: NotificationCommentAction;
  comment: NotificationCommentItem;
  parentComment: NotificationCommentItem;
  author: User;
}

export interface NotificationMention extends Notification {
  author: User;
  story?: Story;
  storyCard?: StoryCard;
}

export interface NotificationLeaderBoard extends Notification {
  title: string;
  message: string;
}

enum InAppMessageLayout {
  CARD = 'CARD',
}

export interface InAppMessage extends InAppMessageContent {
  created: string;
  delivery: string;
  expire: string;
  layout: string;
  placement: InAppMessagePlacement;
  placementPosition: number;
  permissions: { allowToDelete: boolean };
  statistic: { open: number; sent: number };
  channelId?: number;
  image?: ImageFile;
  imageId?: number;
}

export interface ChannelNotificationsArgs {
  channelId: number;
  type: NotificationType[];
  page?: number;
  size?: number;
}

export interface ChannelNotificationsPayload {
  items: (
    | NotificationDefault
    | NotificationComment
    | NotificationMention
    | NotificationLeaderBoard
  )[];
  pageInfo: PaginationInfo;
}

interface ChannelNotificationsResponse {
  data: { channelNotifications: ChannelNotificationsPayload };
  errors?: [{ message: string }];
}

export interface PushNotificationsArgs {
  channelId?: number;
  filters?: { query: string; excludeOrganisationNotifications?: boolean };
  orderDirection?: OrderDirection;
  page?: number;
  size?: number;
}

interface PushNotificationsPayload {
  items: NotificationDefault[];
  pageInfo: PaginationInfo;
}

interface PushNotificationsResponse {
  data: {
    pushNotifications: PushNotificationsPayload;
  };
  errors?: [{ message: string }];
}

export interface PushNotificationDeleteArgs {
  notificationId: number;
}

interface PushNotificationDeleteResponse {
  data: {
    pushNotificationDelete: {
      payload: boolean;
    };
  };
}

export interface PushNotificationChannelCreateArgs {
  channelId: number;
  title: string;
  message: string;
  deliveryTime?: string;
  url?: string;
  storyId?: number;
  itemId?: number;
  eventId?: number;
}

interface PushNotificationChannelCreateResponse {
  data: {
    pushNotificationChannelCreate: {
      payload: NotificationDefault;
    };
  };
}

export interface PushNotificationOrganisationCreateArgs {
  title: string;
  message: string;
  deliveryTime?: string;
  url?: string;
}

interface PushNotificationOrganisationCreateResponse {
  data: {
    pushNotificationOrganisationCreate: {
      payload: NotificationDefault;
    };
  };
}

export interface InAppMessagesArgs {
  channelId?: number;
  filters?: { query: string; excludeOrganisationMessages?: boolean };
  orderDirection?: OrderDirection;
  page?: number;
  size?: number;
}

interface InAppMessagesPayload {
  items: InAppMessage[];
  pageInfo: PaginationInfo;
}

interface InAppMessagesResponse {
  data: {
    inAppMessages: InAppMessagesPayload;
  };
  errors?: [{ message: string }];
}

export interface InAppMessageDeleteArgs {
  inAppMessageId: number;
}

interface InAppMessageDeleteResponse {
  data: {
    inAppMessageDelete: {
      payload: boolean;
    };
  };
}

export interface InAppMessageCreateArgs {
  title: string;
  intro: string;
  textColor: string;
  bgColor: string;
  expirationHours: number;
  buttonLinkType: string;
  buttonText: string;
  buttonTextColor: string;
  buttonBackgroundColor: string;
  placement: InAppMessagePlacement;
  placementPosition?: number;
  deliveryTime?: string;
  imageId?: number;
  url?: string;
  avatar?: boolean;
  position?: string[];
  department?: string[];
  roles?: number[];
  channels?: number[];
}

export interface InAppMessageChannelCreateArgs extends InAppMessageCreateArgs {
  channelId: number;
}

interface InAppMessageChannelCreateResponse {
  data: {
    inAppMessageChannelCreate: {
      payload: InAppMessage;
    };
  };
}

interface InAppMessageOrganisationCreateResponse {
  data: {
    inAppMessageOrganisationCreate: {
      payload: InAppMessage;
    };
  };
}

export const notificationsApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    channelNotifications: builder.query<ChannelNotificationsPayload, ChannelNotificationsArgs>({
      query: ({ channelId, type, page = 1, size = 10 }) => ({
        url: '/graphql/webapp?channelNotifications',
        method: 'POST',
        body: {
          query: `query ChannelNotifications($channelId: Int!, $page: Int, $size: Int, $filter: GetChannelNotificationsFilterArgs) {
            channelNotifications(channelId: $channelId, page: $page, size: $size, filter: $filter) { 
              items {
                ... on NotificationDefault {
                  id
                  type
                  deliveredTime
                  title
                  message
                  url
                  story {
                    id
                    channelId
                  }
                  storyCard {
                    id
                    type
                    content {
                      ... on StoryCardQuoteContent {
                        url
                      }
                      ... on StoryCardArticleContent {
                        url
                      }
                    }
                  }
                  event {
                    id
                  }
                }
                ... on NotificationComment {
                  id
                  type
                  deliveredTime
                  action
                  author {
                    id
                    screenName
                    avatar {
                      averageColor
                      externalId
                      externalType
                      id
                      originalFilename
                      rightholder
                      status
                      statusCopyrightId
                      thumb
                      url
                    }
                  }
                  comment {
                    id
                    content
                    story {
                      id
                    }
                    storyCard {
                      id
                      type
                    }
                  }
                  parentComment {
                    content
                  }
                }
                ... on NotificationStoryCardMention {
                  id
                  type
                  deliveredTime
                  author {
                    id
                    screenName
                    avatar {
                      averageColor
                      externalId
                      externalType
                      id
                      originalFilename
                      rightholder
                      status
                      statusCopyrightId
                      thumb
                      url
                    }
                  }
                  story {
                    id
                  }
                  storyCard {
                    id
                    type
                    category {
                      name
                    }
                    content {
                      ... on StoryCardThreadContent {
                        title
                      }
                      ... on StoryCardEditorialContent {
                        headline
                      }
                      ... on StoryCardImageContent {
                        headline
                      }
                      ... on StoryCardAudioContent {
                        headline
                      }
                      ... on StoryCardVideoContent {
                        headline
                      }
                      ... on StoryCardPdfContent {
                        headline
                      }
                      ... on StoryCardArticleContent {
                        title
                      }
                      ... on StoryCardPostContent {
                        title
                      }
                      ... on StoryCardQuoteContent {
                        quote
                      }
                      ... on StoryCardPollContent {
                        headline
                      }
                    }
                  }
                }
                ... on LeaderBoardUserCloseToPromotionUserNotification {
                  id
                  type
                  deliveredTime
                  title
                  message
                }
                ... on LeaderBoardUserPromotedToNewBadgeUserNotification {
                  id
                  type
                  deliveredTime
                  title
                  message
                }
                ... on ReferralUserActivatedNotification {
                  id
                  type
                  deliveredTime
                  title
                  message
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            filter: { type },
            page,
            size,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: ChannelNotificationsResponse) => {
        if (response?.errors?.length) {
          return {
            items: [],
            pageInfo: {
              page: 0,
              hasNextPage: false,
            } as PaginationInfo,
          };
        }
        return response.data.channelNotifications;
      },
    }),
    pushNotifications: builder.query<PushNotificationsPayload, PushNotificationsArgs>({
      query: ({
        channelId,
        filters,
        orderDirection = OrderDirection.DESC,
        page = 1,
        size = 10,
      }) => ({
        url: '/graphql/webapp?pushNotifications',
        method: 'POST',
        body: {
          query: `query PushNotifications($channelId: Int, $filters: GetPushNotificationsFilterArgs, $orderDirection: OrderDirectionEnum!, $page: Int, $size: Int) {
            pushNotifications(channelId: $channelId, filters: $filters, orderDirection: $orderDirection, page: $page, size: $size) { 
              items {
                id
                channelId
                title
                message
                url
                createdTime
                deliveredTime
                expirationTime
                statistic {
                  open
                  sent
                }
                permissions {
                  allowToDelete
                }
                storyCard {
                  storyId
                  id
                }
                event {
                  id
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            filters,
            orderDirection,
            page,
            size,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: PushNotificationsResponse) => {
        if (response?.errors?.length) {
          return {
            items: [],
            pageInfo: { page: 0, hasNextPage: false } as PaginationInfo,
          };
        }
        return response.data.pushNotifications;
      },
    }),
    pushNotificationDelete: builder.mutation<boolean, PushNotificationDeleteArgs>({
      query: ({ notificationId }) => ({
        url: '/graphql/webapp?pushNotificationDelete',
        method: 'POST',
        body: {
          query: `mutation PushNotificationDelete($input: PushNotificationDeleteArgs!) {
            pushNotificationDelete(input: $input) {
              payload
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: { input: { notificationId } },
        },
      }),
      transformResponse: (response: PushNotificationDeleteResponse) =>
        response.data.pushNotificationDelete.payload,
    }),
    pushNotificationChannelCreate: builder.mutation<
      NotificationDefault,
      PushNotificationChannelCreateArgs
    >({
      query: ({ channelId, title, message, url, deliveryTime, storyId, itemId, eventId }) => ({
        url: '/graphql/webapp?pushNotificationChannelCreate',
        method: 'POST',
        body: {
          query: `mutation PushNotificationChannelCreate($input: PushNotificationChannelCreateInput!) {
            pushNotificationChannelCreate(input: $input) {
              payload {
                id
              }
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: {
              channelId,
              fields: {
                title,
                message,
                ...(Boolean(url) && { url }),
                ...(Boolean(deliveryTime) && { deliveryTime }),
                ...(Boolean(storyId) && { storyId }),
                ...(Boolean(itemId) && { itemId }),
                ...(Boolean(eventId) && { eventId }),
              },
            },
          },
        },
      }),
      transformResponse: (response: PushNotificationChannelCreateResponse) =>
        response.data.pushNotificationChannelCreate.payload,
    }),
    pushNotificationOrganisationCreate: builder.mutation<
      NotificationDefault,
      PushNotificationOrganisationCreateArgs
    >({
      query: ({ title, message, url, deliveryTime }) => ({
        url: '/graphql/webapp?pushNotificationOrganisationCreate',
        method: 'POST',
        body: {
          query: `mutation PushNotificationOrganisationCreate($input: PushNotificationOrganisationCreateInput!) {
            pushNotificationOrganisationCreate(input: $input) {
              payload {
                id
              }
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: {
              fields: {
                title,
                message,
                ...(Boolean(url) && { url }),
                ...(Boolean(deliveryTime) && { deliveryTime }),
              },
            },
          },
        },
      }),
      transformResponse: (response: PushNotificationOrganisationCreateResponse) =>
        response.data.pushNotificationOrganisationCreate.payload,
    }),
    inAppMessages: builder.query<InAppMessagesPayload, InAppMessagesArgs>({
      query: ({
        channelId,
        filters,
        orderDirection = OrderDirection.DESC,
        page = 1,
        size = 10,
      }) => ({
        url: '/graphql/webapp?inAppMessages',
        method: 'POST',
        body: {
          query: `query InAppMessages($channelId: Int, $filters: GetInAppMessagesFilterArgs, $orderDirection: OrderDirectionEnum!, $page: Int, $size: Int) {
            inAppMessages(channelId: $channelId, filters: $filters, orderDirection: $orderDirection, page: $page, size: $size) { 
              items {
                id
                title
                intro
                created
                delivery
                expire
                channelId
                placement
                placementPosition
                permissions {
                  allowToDelete
                }
                statistic {
                  open
                  sent
                }
                primary {
                  action
                }
                image {
                  url
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            filters,
            orderDirection,
            page,
            size,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: InAppMessagesResponse) => {
        if (response?.errors?.length) {
          return {
            items: [],
            pageInfo: { page: 0, hasNextPage: false } as PaginationInfo,
          };
        }
        return response.data.inAppMessages;
      },
    }),
    inAppMessageDelete: builder.mutation<boolean, InAppMessageDeleteArgs>({
      query: ({ inAppMessageId }) => ({
        url: '/graphql/webapp?inAppMessageDelete',
        method: 'POST',
        body: {
          query: `mutation InAppMessageDelete($input: InAppMessageDeleteArgs!) {
            inAppMessageDelete(input: $input) {
              payload
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: { input: { inAppMessageId } },
        },
      }),
      transformResponse: (response: InAppMessageDeleteResponse) =>
        response.data.inAppMessageDelete.payload,
    }),
    inAppMessageChannelCreate: builder.mutation<InAppMessage, InAppMessageChannelCreateArgs>({
      query: ({
        channelId,
        title,
        intro,
        textColor,
        bgColor,
        buttonLinkType,
        buttonText,
        buttonTextColor,
        buttonBackgroundColor,
        placement,
        placementPosition,
        deliveryTime,
        expirationHours,
        imageId,
        url,
        avatar,
        roles,
        position,
        department,
      }) => ({
        url: '/graphql/webapp?inAppMessageChannelCreate',
        method: 'POST',
        body: {
          query: `mutation InAppMessageChannelCreate($input: InAppMessageChannelCreateInput!) {
            inAppMessageChannelCreate(input: $input) {
              payload {
                id
              }
              error {
                __typename
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: {
              channelId,
              fields: {
                title,
                intro,
                textColor,
                bgColor,
                primary: {
                  text: buttonText,
                  textColor: buttonTextColor,
                  bgColor: buttonBackgroundColor,
                  action: {
                    ...(Boolean(buttonLinkType === ButtonLinkType.URL_BROWSER) && {
                      openExternalAction: { url },
                    }),
                    ...(Boolean(buttonLinkType === ButtonLinkType.URL_APP) && {
                      openUrlAction: { url },
                    }),
                    ...(Boolean(buttonLinkType === ButtonLinkType.SHARE) && {
                      shareAction: { url },
                    }),
                  },
                },
                expirationHours,
                layout: InAppMessageLayout.CARD,
                placement,
                ...(Boolean(placementPosition) && { placementPosition }),
                conditions: {
                  types: 'login',
                  ...(Boolean(typeof avatar !== 'undefined') && { avatar }),
                  ...(Boolean(roles) && { roles }),
                  ...(Boolean(position) && { position }),
                  ...(Boolean(department) && { department }),
                },
                ...(Boolean(imageId) && { imageId }),
                ...(Boolean(deliveryTime) && { deliveryTime }),
              },
            },
          },
        },
      }),
      transformResponse: (response: InAppMessageChannelCreateResponse) =>
        response.data.inAppMessageChannelCreate.payload,
    }),
    inAppMessageOrganisationCreate: builder.mutation<InAppMessage, InAppMessageCreateArgs>({
      query: ({
        title,
        intro,
        textColor,
        bgColor,
        buttonLinkType,
        buttonText,
        buttonTextColor,
        buttonBackgroundColor,
        placement,
        placementPosition,
        deliveryTime,
        expirationHours,
        imageId,
        url,
        avatar,
        roles,
        position,
        department,
        channels,
      }) => ({
        url: '/graphql/webapp?inAppMessageOrganisationCreate',
        method: 'POST',
        body: {
          query: `mutation InAppMessageOrganisationCreate($input: InAppMessageOrganisationCreateInput!) {
            inAppMessageOrganisationCreate(input: $input) {
              payload {
                id
              }
              error {
                ... on UnknownError {
                  message
                }
              }
            }
          }`,
          variables: {
            input: {
              fields: {
                title,
                intro,
                textColor,
                bgColor,
                primary: {
                  text: buttonText,
                  textColor: buttonTextColor,
                  bgColor: buttonBackgroundColor,
                  action: {
                    ...(Boolean(buttonLinkType === ButtonLinkType.URL_BROWSER) && {
                      openExternalAction: { url },
                    }),
                    ...(Boolean(buttonLinkType === ButtonLinkType.URL_APP) && {
                      openUrlAction: { url },
                    }),
                    ...(Boolean(buttonLinkType === ButtonLinkType.SHARE) && {
                      shareAction: { url },
                    }),
                  },
                },
                expirationHours,
                layout: InAppMessageLayout.CARD,
                placement,
                ...(Boolean(placementPosition) && { placementPosition }),
                conditions: {
                  types: 'login',
                  ...(Boolean(typeof avatar !== 'undefined') && { avatar }),
                  ...(Boolean(roles) && { roles }),
                  ...(Boolean(position) && { position }),
                  ...(Boolean(department) && { department }),
                  ...(Boolean(channels) && { channels }),
                },
                ...(Boolean(imageId) && { imageId }),
                ...(Boolean(deliveryTime) && { deliveryTime }),
              },
            },
          },
        },
      }),
      transformResponse: (response: InAppMessageOrganisationCreateResponse) =>
        response.data.inAppMessageOrganisationCreate.payload,
    }),
  }),
});

export const { useChannelNotificationsQuery, usePushNotificationsQuery } = notificationsApi;
